/*
 * ============================================================================
 * HAOUA — Haoua Shipping Protection
 * Personnalisation maison, ne fait pas partie de Prestige.
 *
 * Pilote la case à cocher rendue par snippets/shipping-protection.liquid :
 *   - case DÉCOCHÉE par défaut, la cliente coche pour ajouter la protection ;
 *   - retire la protection si le panier ne contient plus rien à expédier
 *     (une protection livraison toute seule dans un panier n'a pas de sens).
 *
 * CHARGÉ DEPUIS snippets/js-variables.liquid (donc <head>, sur toutes les
 * pages) ET PAS DEPUIS LE SNIPPET DU PANIER : quand Prestige rafraîchit le
 * tiroir panier il en réécrit le HTML, et un <script> réinjecté de cette
 * manière ne s'exécute jamais.
 *
 * LA VÉRITÉ VIENT DE /cart.js, PAS DU DOM. Sur la page panier il y a DEUX
 * blocs — celui du tiroir et celui de la page — et Prestige ne rafraîchit que
 * le tiroir. Se fier au DOM revenait à lire un bloc à jour pendant que l'autre
 * affichait encore l'ancien total. Le DOM ne sert donc qu'à connaître l'ID de
 * variante et à vérifier, après coup, que TOUS les blocs sont à jour.
 *
 * À RÉAPPLIQUER APRÈS CHAQUE MISE À JOUR DE PRESTIGE.
 * ============================================================================
 */

(() => {
  if (window.__haouaShippingProtection) return;
  window.__haouaShippingProtection = true;

  const SELECTOR = '[data-haoua-sp]';
  const SYNC_THROTTLE = 800;

  let busy = false;
  let lastSync = 0;
  let debounce;

  const blocks = () => [...document.querySelectorAll(SELECTOR)];
  const onCartPage = () => /\/cart(\/|$)/.test(window.location.pathname);

  async function postJSON(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      throw new Error(`${url} a répondu ${response.status}`);
    }

    return response.json();
  }

  const getCart = () => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

  const addProtection = (variantId) =>
    postJSON('/cart/add.js', { items: [{ id: variantId, quantity: 1 }] });

  const removeProtection = (lineKey) =>
    postJSON('/cart/change.js', { id: lineKey, quantity: 0 });

  /*
   * Lit l'état réel du panier. `shippable` ignore volontairement la protection
   * elle-même : un panier qui ne contiendrait qu'elle n'a rien à protéger.
   */
  async function readState(variantId) {
    const cart = await getCart();
    const line = cart.items.find((item) => item.variant_id === variantId);

    return {
      inCart: Boolean(line),
      lineKey: line ? line.key : '',
      shippable: cart.items.some((item) => item.variant_id !== variantId && item.requires_shipping)
    };
  }

  /*
   * Sur la page panier on recharge : le tableau des articles et la ligne Total
   * sont rendus par Liquid et Prestige ne les rafraîchit pas. Ailleurs, on
   * laisse d'abord Prestige faire (il met à jour le compteur du header et la
   * barre de livraison offerte), puis on vérifie.
   */
  async function refresh(expectedInCart) {
    if (onCartPage()) {
      window.location.reload();
      return;
    }

    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    await new Promise((resolve) => setTimeout(resolve, 700));

    const upToDate = () => {
      const all = blocks();
      return all.length > 0 && all.every((el) => (el.dataset.inCart === 'true') === expectedInCart);
    };

    if (upToDate()) return;

    const drawer = document.querySelector('cart-drawer');

    if (drawer) {
      try {
        const sections = await fetch('/?sections=cart-drawer').then((r) => r.json());
        const fresh = new DOMParser()
          .parseFromString(sections['cart-drawer'], 'text/html')
          .querySelector('cart-drawer');

        if (fresh) {
          // On remplace le contenu et non l'élément : le tiroir reste ouvert.
          drawer.innerHTML = fresh.innerHTML;
          if (upToDate()) return;
        }
      } catch (error) {
        console.warn('[Haoua SP] rafraîchissement du tiroir impossible', error);
      }
    }

    window.location.reload();
  }

  /*
   * Seul ménage automatique restant : la case n'étant pas pré-cochée, on
   * n'ajoute jamais rien sans clic. En revanche si la cliente vide son panier
   * de tous ses articles, la protection ne doit pas rester seule.
   */
  async function sync() {
    if (busy) return;

    const el = blocks()[0];
    if (!el) return;

    const now = Date.now();
    if (now - lastSync < SYNC_THROTTLE) return;
    lastSync = now;

    const variantId = Number(el.dataset.variantId);
    if (!variantId) return;

    let state;
    try {
      state = await readState(variantId);
    } catch (error) {
      console.warn('[Haoua SP]', error);
      return;
    }

    if (busy || state.shippable || !state.inCart) return;

    busy = true;
    try {
      await removeProtection(state.lineKey);
      await refresh(false);
    } catch (error) {
      console.warn('[Haoua SP]', error);
    } finally {
      busy = false;
    }
  }

  document.addEventListener('change', async (event) => {
    const input = event.target.closest('.haoua-sp__checkbox');
    if (!input) return;

    const el = input.closest(SELECTOR);
    if (!el || busy) return;

    const wanted = input.checked;
    const variantId = Number(el.dataset.variantId);

    busy = true;
    input.disabled = true;

    try {
      const state = await readState(variantId);

      if (wanted && !state.inCart) {
        await addProtection(variantId);
      } else if (!wanted && state.inCart) {
        await removeProtection(state.lineKey);
      }

      await refresh(wanted);
    } catch (error) {
      console.warn('[Haoua SP]', error);
      input.checked = !wanted;
      input.disabled = false;
    } finally {
      busy = false;
    }
  });

  const schedule = () => {
    clearTimeout(debounce);
    debounce = setTimeout(sync, 150);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }

  // Le tiroir panier est re-rendu à chaque ajout produit : on réévalue alors.
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
})();